/* Keyboard for the panels that pick a count per unit — the march picker and a city's production
   queue (owner, 2026-09-29): Tab in a count field goes to the next count field, Shift+Tab to the
   previous, past the unit's button and slider between them. At either end the key falls through to
   the dialog's own focus order. */
export const tabBetweenFields = (selector) => (event) => {
  if (event.key !== "Tab") return
  const scope = event.currentTarget.closest("[role=dialog]") || document.body
  const fields = [...scope.querySelectorAll(selector)]
  const next = fields[fields.indexOf(event.currentTarget) + (event.shiftKey ? -1 : 1)]
  if (!next || next.disabled) return
  event.preventDefault()
  // The dialog's focus trap would otherwise read the moved focus and wrap it again.
  event.stopPropagation()
  next.focus()
  next.select?.()
}

/* The first Tab in a dialog lands on its first count field (owner, 2026-09-29): a dialog opens
   with the focus on its box (Modal.js — never on a field, which would open a phone's keyboard),
   and the first Tab used to reach the close button and the building cards before any count.
   Only while the box itself holds the focus; after that Tab is the ordinary order.

   And from outside every dialog (owner, 2026-10-07: "When we open a modal, the context for the
   click should be that modal"). The city sheet leaves the side bar and the map pressable, so a
   press on a roster row with the sheet open moved the focus out of it — to the row's tile, or to
   the page — and Tab then walked the roster under a sheet the player was looking at. With the
   focus in no dialog, Tab goes to the top sheet's first count and Shift+Tab to its last. A sheet
   kept mounted under another (`.modal-layer-hidden`) is not the top one and leaves the key alone.
   Returns the listener's remover, for an effect. */
export const focusFirstFieldOnTab = (panel, selector) => {
  if (!panel) return () => {}
  const onKeyDown = (event) => {
    if (event.key !== "Tab") return
    if (panel.closest(".modal-layer")?.classList.contains("modal-layer-hidden")) return
    const focused = document.activeElement
    const onTheBox = focused?.getAttribute?.("role") === "dialog" && focused.contains(panel)
    const outside = !focused || !focused.closest?.("[role=dialog]")
    if (!(onTheBox && !event.shiftKey) && !outside) return
    const fields = [...panel.querySelectorAll(selector)].filter((field) => !field.disabled)
    const target = outside && event.shiftKey ? fields[fields.length - 1] : fields[0]
    if (!target) return
    event.preventDefault()
    event.stopPropagation()
    target.focus()
    target.select?.()
  }
  // Capture, so it runs before the dialog's focus trap reads the same key.
  window.addEventListener("keydown", onKeyDown, true)
  return () => window.removeEventListener("keydown", onKeyDown, true)
}
